// ============================================================================
//  GanttPage.tsx — Vue Gantt NexusCorp ERP
//  Bloc C — Timeline SVG native, dépendances, sprints, jalons
// ============================================================================

import { useState, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, ZoomIn, ZoomOut, ChevronRight, ChevronDown,
  AlertTriangle, CheckCircle, Clock, Calendar, Link,
  Flag, User, Layers,
} from 'lucide-react'
import api from '@/lib/axios'

// ─── Types ───────────────────────────────────────────────────────────────────

interface GanttTask {
  id:             string
  number:         number
  title:          string
  type:           string
  priority:       string
  status:         { id: string; name: string; color: string; isDone: boolean }
  assignee?:      { id: string; firstName: string; lastName: string; avatarUrl?: string }
  sprint?:        { id: string; name: string; status: string; startDate?: string; endDate?: string }
  parentId?:      string
  startDate?:     string
  dueDate?:       string
  completedAt?:   string
  estimatedHours?: number
  loggedHours:    number
  progress:       number
  storyPoints?:   number
  dependsOn:      Array<{ id: string; blockingTaskId: string; blockingTask: { id: string; number: number; title: string } }>
  blocks:         Array<{ id: string; dependentTaskId: string; dependentTask: { id: string; number: number; title: string } }>
}

interface GanttData {
  project:  { id: string; name: string; startDate?: string; endDate?: string }
  sprints:  Array<{ id: string; name: string; status: string; startDate?: string; endDate?: string }>
  rows:     GanttTask[]
  viewport: { startDate: string; endDate: string; totalDays: number }
  stats:    { total: number; withDates: number; completed: number; overdue: number }
}

// ─── Config ───────────────────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  LOW:    '#64748b',
  MEDIUM: '#3b82f6',
  HIGH:   '#f59e0b',
  URGENT: '#ef4444',
}

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'

// ─── Helpers dates ────────────────────────────────────────────────────────────

function daysBetween(a: Date, b: Date) {
  return Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24))
}

function addDays(date: Date, days: number) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ user }: { user?: { firstName: string; lastName: string; avatarUrl?: string } }) {
  if (!user) return <div className="w-5 h-5 rounded-full bg-[var(--surface-2)] shrink-0" />
  const initials = `${user.firstName[0]}${user.lastName[0]}`
  return user.avatarUrl
    ? <img src={user.avatarUrl} className="w-5 h-5 rounded-full object-cover shrink-0" />
    : <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--tx)] text-[8px] font-bold shrink-0">{initials}</div>
}

// ============================================================================
//  GANTT PAGE
// ============================================================================

export default function GanttPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate           = useNavigate()

  // Zoom : pixels par jour
  const [dayWidth,   setDayWidth]   = useState(28)
  const [rowHeight,  setRowHeight]  = useState(36)
  const [collapsed,  setCollapsed]  = useState<Set<string>>(new Set())
  const [selected,   setSelected]   = useState<string | null>(null)
  const [groupBy,    setGroupBy]    = useState<'sprint' | 'none'>('sprint')
  const [showNoDates, setShowNoDates] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, error } = useQuery<GanttData>({
    queryKey: ['gantt', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/gantt`).then(r => r.data.data ?? r.data),
    enabled:  !!projectId,
  })

  const viewStart = useMemo(() =>
    data ? new Date(data.viewport.startDate) : new Date(),
    [data]
  )

  // Tâches filtrées
  const visibleRows = useMemo(() => {
    if (!data) return []
    return showNoDates
      ? data.rows
      : data.rows.filter(t => t.startDate || t.dueDate)
  }, [data, showNoDates])

  // Grouper par sprint
  const groups = useMemo(() => {
    if (!data || groupBy !== 'sprint') return null

    const sprintMap = new Map<string, { sprint: typeof data.sprints[0] | null; tasks: GanttTask[] }>()
    sprintMap.set('__backlog__', { sprint: null, tasks: [] })
    for (const s of data.sprints) sprintMap.set(s.id, { sprint: s, tasks: [] })

    for (const task of visibleRows) {
      const key = task.sprint?.id ?? '__backlog__'
      const grp = sprintMap.get(key) ?? sprintMap.get('__backlog__')!
      grp.tasks.push(task)
    }

    return Array.from(sprintMap.entries())
      .filter(([, g]) => g.tasks.length > 0)
      .map(([id, g]) => ({ id, sprint: g.sprint, tasks: g.tasks }))
  }, [data, visibleRows, groupBy])

  // Convertir une date en position X dans la timeline
  const dateToX = useCallback((d: string) => {
    return daysBetween(viewStart, new Date(d)) * dayWidth
  }, [viewStart, dayWidth])

  if (isLoading) return <div className="flex items-center justify-center h-40"><Spinner /></div>
  if (error || !data) return (
    <div className="flex items-center justify-center h-40 text-[var(--red-text)]">
      Erreur de chargement du Gantt
    </div>
  )

  const totalWidth = data.viewport.totalDays * dayWidth

  // Générer les colonnes de la timeline (semaines)
  const timelineHeaders: Array<{ label: string; x: number; isMonthStart: boolean }> = []
  for (let i = 0; i <= data.viewport.totalDays; i += 7) {
    const d = addDays(viewStart, i)
    const isMonthStart = d.getDate() <= 7 && (i === 0 || addDays(viewStart, i - 7).getMonth() !== d.getMonth())
    timelineHeaders.push({
      label: isMonthStart
        ? d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
        : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      x: i * dayWidth,
      isMonthStart,
    })
  }

  // Aujourd'hui
  const todayX = daysBetween(viewStart, new Date()) * dayWidth

  // ── Render ────────────────────────────────────────────────────────────────

  const renderRow = (task: GanttTask, rowIndex: number, indent = 0) => {
    const y    = rowIndex * rowHeight
    const isOverdue = task.dueDate && !task.completedAt && new Date(task.dueDate) < new Date()
    const isSelected = selected === task.id

    // Barre Gantt
    let barX = 0, barW = 0
    if (task.startDate && task.dueDate) {
      barX = dateToX(task.startDate)
      barW = Math.max(dayWidth, daysBetween(new Date(task.startDate), new Date(task.dueDate)) * dayWidth)
    } else if (task.dueDate) {
      barX = dateToX(task.dueDate) - dayWidth
      barW = dayWidth * 2
    } else if (task.startDate) {
      barX = dateToX(task.startDate)
      barW = dayWidth * 2
    }

    const barColor = task.completedAt
      ? '#10b981'
      : isOverdue
      ? '#ef4444'
      : PRIORITY_COLORS[task.priority] ?? '#3b82f6'

    return (
      <g key={task.id} onClick={() => setSelected(isSelected ? null : task.id)}>
        {/* Fond de ligne */}
        <rect
          x={0} y={y}
          width={totalWidth} height={rowHeight - 1}
          fill={isSelected ? 'rgba(59,130,246,0.08)' : rowIndex % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}
          className="cursor-pointer"
        />

        {/* Barre principale */}
        {barW > 0 && (
          <>
            <rect
              x={barX} y={y + 7}
              width={barW} height={rowHeight - 14}
              rx={3}
              fill={barColor}
              opacity={0.8}
              className="cursor-pointer"
            />
            {/* Progression */}
            {task.progress > 0 && task.progress < 100 && (
              <rect
                x={barX} y={y + 7}
                width={barW * task.progress / 100} height={rowHeight - 14}
                rx={3}
                fill={barColor}
                opacity={1}
              />
            )}
            {/* Label dans la barre */}
            {barW > 60 && (
              <text
                x={barX + 6} y={y + rowHeight / 2 + 4}
                fill="white" fontSize={10} fontWeight={500}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {task.title.length > 20 ? task.title.slice(0, 18) + '…' : task.title}
              </text>
            )}
          </>
        )}

        {/* Point/milestone si pas de durée */}
        {barW === 0 && task.dueDate && (
          <polygon
            points={`${dateToX(task.dueDate)},${y + 7} ${dateToX(task.dueDate) + 8},${y + rowHeight / 2} ${dateToX(task.dueDate)},${y + rowHeight - 7} ${dateToX(task.dueDate) - 8},${y + rowHeight / 2}`}
            fill={PRIORITY_COLORS[task.priority]}
            opacity={0.9}
          />
        )}
      </g>
    )
  }

  // Flèches dépendances (SVG)
  const renderDependencies = () => {
    return visibleRows.flatMap(task => {
      if (!task.dueDate) return []
      return task.dependsOn.map(dep => {
        const fromTask = visibleRows.find(t => t.id === dep.blockingTaskId)
        if (!fromTask?.dueDate) return null

        const fromIdx = visibleRows.indexOf(fromTask)
        const toIdx   = visibleRows.indexOf(task)
        if (fromIdx < 0 || toIdx < 0) return null

        const x1 = dateToX(fromTask.dueDate!) + dayWidth
        const y1 = fromIdx * rowHeight + rowHeight / 2
        const x2 = dateToX(task.startDate ?? task.dueDate!)
        const y2 = toIdx * rowHeight + rowHeight / 2

        const cx = (x1 + x2) / 2

        return (
          <path
            key={dep.id}
            d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="#94a3b8"
            strokeWidth={1.5}
            strokeDasharray="4 2"
            markerEnd="url(#arrowhead)"
            opacity={0.6}
          />
        )
      }).filter(Boolean)
    })
  }

  const svgHeight = visibleRows.length * rowHeight + 32
  const LABEL_WIDTH = 260

  return (
    <div className="h-screen flex flex-col bg-[var(--surface-2)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bd)] bg-white/80 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/projects/${projectId}`)} className="text-[var(--tx3)] hover:text-[var(--tx)]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base font-bold text-[var(--tx)] flex items-center gap-2">
              <Layers className="w-4 h-4 text-[var(--blue-d)]" />
              {data.project.name} — Gantt
            </h1>
            <p className="text-xs text-[var(--tx3)]">
              {data.stats.withDates}/{data.stats.total} tâches planifiées ·
              {data.stats.overdue > 0 && <span className="text-[var(--red-text)] ml-1">{data.stats.overdue} en retard</span>}
              <span className="ml-1">{data.stats.completed} terminées</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Filtre tâches sans dates */}
          <label className="flex items-center gap-2 text-xs text-[var(--tx3)] cursor-pointer">
            <input
              type="checkbox"
              checked={showNoDates}
              onChange={e => setShowNoDates(e.target.checked)}
              className="accent-blue-500"
            />
            Sans dates
          </label>

          {/* Groupement */}
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value as any)}
            className="px-2 py-1 bg-white border border-[var(--bd)] rounded text-xs text-[var(--tx2)] focus:outline-none"
          >
            <option value="sprint">Par sprint</option>
            <option value="none">Sans groupement</option>
          </select>

          {/* Zoom */}
          <div className="flex items-center gap-1 bg-white border border-[var(--bd)] rounded-lg px-2 py-1">
            <button onClick={() => setDayWidth(d => Math.max(8, d - 8))} className="text-[var(--tx3)] hover:text-[var(--tx)]">
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-[var(--tx3)] w-8 text-center">{dayWidth}px</span>
            <button onClick={() => setDayWidth(d => Math.min(80, d + 8))} className="text-[var(--tx3)] hover:text-[var(--tx)]">
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Corps — sidebar labels + SVG timeline */}
      <div className="flex flex-1 overflow-hidden">

        {/* Labels column */}
        <div className="w-64 shrink-0 flex flex-col border-r border-[var(--bd)] bg-[var(--surface-2)]">
          {/* Header labels */}
          <div className="h-8 bg-white border-b border-[var(--bd)] flex items-center px-3">
            <span className="text-xs font-medium text-[var(--tx3)]">Tâche</span>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {(groups ?? [{ id: 'all', sprint: null, tasks: visibleRows }]).map(group => {
              const isGroupCollapsed = collapsed.has(group.id)
              return (
                <div key={group.id}>
                  {/* Group header */}
                  {groups && (
                    <div
                      onClick={() => setCollapsed(s => {
                        const n = new Set(s)
                        n.has(group.id) ? n.delete(group.id) : n.add(group.id)
                        return n
                      })}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white/60 border-b border-[var(--bd)] cursor-pointer hover:bg-white"
                    >
                      {isGroupCollapsed ? <ChevronRight className="w-3 h-3 text-[var(--tx3)]" /> : <ChevronDown className="w-3 h-3 text-[var(--tx3)]" />}
                      <span className="text-xs font-semibold text-[var(--tx2)]">
                        {group.sprint?.name ?? '📋 Backlog'}
                      </span>
                      <span className="text-[10px] text-[var(--tx3)] ml-auto">{group.tasks.length}</span>
                    </div>
                  )}
                  {/* Task rows */}
                  {!isGroupCollapsed && group.tasks.map(task => {
                    const isOverdue = task.dueDate && !task.completedAt && new Date(task.dueDate) < new Date()
                    return (
                      <div
                        key={task.id}
                        onClick={() => setSelected(selected === task.id ? null : task.id)}
                        className={`flex items-center gap-2 px-3 cursor-pointer transition-colors border-b border-[var(--bd)] ${
                          selected === task.id ? 'bg-[var(--blue)]/10' : 'hover:bg-[var(--surface-2)]'
                        }`}
                        style={{ height: rowHeight }}
                      >
                        <div
                          className="w-1.5 h-4 rounded-full shrink-0"
                          style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs truncate ${task.completedAt ? 'line-through text-[var(--tx3)]' : isOverdue ? 'text-[var(--red-text)]' : 'text-[var(--tx)]'}`}>
                            {task.title}
                          </p>
                          <p className="text-[10px] text-[var(--tx4)] truncate">
                            #{task.number} · {task.status.name}
                          </p>
                        </div>
                        <Avatar user={task.assignee} />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>

        {/* Timeline SVG */}
        <div ref={scrollRef} className="flex-1 overflow-auto">
          <svg
            width={totalWidth}
            height={Math.max(400, svgHeight + 40)}
            style={{ display: 'block', minWidth: totalWidth }}
          >
            <defs>
              <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                <polygon points="0 0, 6 3, 0 6" fill="#94a3b8" opacity={0.6} />
              </marker>
            </defs>

            {/* Timeline header */}
            <rect x={0} y={0} width={totalWidth} height={32} fill="#0f172a" />
            {timelineHeaders.map(h => (
              <g key={h.x}>
                <line x1={h.x} y1={0} x2={h.x} y2={32} stroke="rgba(255,255,255,0.06)" />
                <text
                  x={h.x + 4} y={h.isMonthStart ? 20 : 18}
                  fill={h.isMonthStart ? '#94a3b8' : '#475569'}
                  fontSize={h.isMonthStart ? 11 : 9}
                  fontWeight={h.isMonthStart ? 600 : 400}
                >
                  {h.label}
                </text>
              </g>
            ))}

            {/* Lignes de grille verticales */}
            {timelineHeaders.map(h => (
              <line
                key={`grid-${h.x}`}
                x1={h.x} y1={32} x2={h.x} y2={svgHeight + 40}
                stroke="rgba(255,255,255,0.04)"
              />
            ))}

            {/* Barres sprints (fond) */}
            {data.sprints.filter(s => s.startDate && s.endDate).map(sprint => {
              const sx = dateToX(sprint.startDate!)
              const sw = daysBetween(new Date(sprint.startDate!), new Date(sprint.endDate!)) * dayWidth
              return (
                <rect
                  key={sprint.id}
                  x={sx} y={32}
                  width={sw} height={svgHeight + 8}
                  fill={sprint.status === 'ACTIVE' ? 'rgba(99,102,241,0.04)' : 'transparent'}
                  stroke={sprint.status === 'ACTIVE' ? 'rgba(99,102,241,0.15)' : 'none'}
                />
              )
            })}

            {/* Tâches */}
            <g transform="translate(0, 32)">
              {visibleRows.map((task, i) => renderRow(task, i))}
              {renderDependencies()}
            </g>

            {/* Ligne "aujourd'hui" */}
            {todayX >= 0 && todayX <= totalWidth && (
              <g>
                <line
                  x1={todayX} y1={0}
                  x2={todayX} y2={svgHeight + 40}
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  opacity={0.6}
                />
                <rect x={todayX - 18} y={0} width={36} height={16} rx={3} fill="#ef4444" opacity={0.9} />
                <text x={todayX} y={11} textAnchor="middle" fill="white" fontSize={9} fontWeight={700}>
                  Auj.
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>

      {/* Panneau détail tâche sélectionnée */}
      {selected && (() => {
        const task = data.rows.find(t => t.id === selected)
        if (!task) return null
        return (
          <div className="absolute bottom-4 right-4 w-72 nx-card shadow-2xl p-4 space-y-3 z-10">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[var(--tx3)]">#{task.number} · {task.type}</p>
                <p className="text-sm font-semibold text-[var(--tx)]">{task.title}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--tx3)] hover:text-[var(--tx)] text-lg leading-none">×</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><p className="text-[var(--tx3)]">Début</p><p className="text-[var(--tx)]">{fmtDate(task.startDate)}</p></div>
              <div><p className="text-[var(--tx3)]">Fin</p><p className={task.dueDate && !task.completedAt && new Date(task.dueDate) < new Date() ? 'text-[var(--red-text)]' : 'text-[var(--tx)]'}>{fmtDate(task.dueDate)}</p></div>
              <div><p className="text-[var(--tx3)]">Avancement</p><p className="text-[var(--tx)]">{task.progress}%</p></div>
              <div><p className="text-[var(--tx3)]">Statut</p><p style={{ color: task.status.color }}>{task.status.name}</p></div>
            </div>
            {task.assignee && (
              <div className="flex items-center gap-2">
                <Avatar user={task.assignee} />
                <span className="text-xs text-[var(--tx2)]">{task.assignee.firstName} {task.assignee.lastName}</span>
              </div>
            )}
            {task.dependsOn.length > 0 && (
              <div>
                <p className="text-xs text-[var(--tx3)] mb-1 flex items-center gap-1"><Link className="w-3 h-3" /> Dépend de</p>
                {task.dependsOn.map(d => (
                  <p key={d.id} className="text-xs text-[var(--blue-d)]">#{d.blockingTask.number} {d.blockingTask.title.slice(0, 25)}</p>
                ))}
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}
